// 批改複習考卷(拍照)— LLM-Wiki 閉環的最後一段 (spec §9 P3)。
//
// Flow:
//   1. loadStoredPaper() 取最近一張已生成的 ReviewPaper;無 → 提示先生成。
//   2. 顯示卷號 + 拍照/選檔 input(沿用 exam-upload 的 <input type=file accept=image>)。
//   3. 拿到照片 → paper.front 轉成 QuizPackWithKey → gradeScannedPaper(file, pack)。
//   4. 顯示分數 (X/N) + 逐題對錯。
//   5. 回流:答錯題 → recordMistake + saveProgress;同時 ingestToWiki(失敗不阻斷)。
//
// 學習/遊戲分離鐵則:只讀寫學習側資料(錯題/wiki),絕不碰戰鬥數值、commanderXp。
// 專業介面:純文字、極簡,無音效、無 mascot;錯誤不白屏。

import { loadProgress, saveProgress } from '../../shared/progress';
import { recordMistake, deriveQuestionId } from '../../shared/mistake-log';
import { ingestToWiki } from '../../shared/wiki-client';
import type { IngestSource } from '../../shared/wiki-client';
import { gradeScannedPaper } from '../printable/scan-grader';
import type { ScanGradeResult } from '../printable/scan-grader';
import type { QuizPackWithKey } from '../printable/quiz-pack';
import type { GeneratedQuestion } from '../generators/math/types';
import { loadStoredPaper } from './review-paper';
import type { ReviewPaper, ReviewQuestion } from './types';

/**
 * Render the photo-grading entry point into `container`.
 * onDone: called when the user exits (back button).
 */
export function runGradeReview(container: HTMLElement, onDone: () => void): void {
  const root = document.createElement('div');
  root.className = 'grade-review';
  container.appendChild(root);

  const paper = loadStoredPaper();
  if (!paper) {
    renderMessage(
      root,
      '還沒有可批改的複習考卷,先到「生成複習考卷」生成一張再來批改。',
      onDone,
    );
    return;
  }

  renderUpload(root, paper, onDone);
}

// ── views ──────────────────────────────────────────────────────────────────

function renderUpload(root: HTMLElement, paper: ReviewPaper, onDone: () => void): void {
  root.innerHTML = `
    <div class="grade-review-upload">
      <h1 style="margin:0 0 4px;">批改複習考卷</h1>
      <p style="margin:0 0 12px;color:#555;">卷號 ${escapeHtml(paper.paperId)} — 拍下或選一張小孩寫完的考卷,系統會自動批改並把答錯的題目加進錯題本。</p>
      <input type="file" accept="image/*" capture="environment" class="grade-file" />
      <div class="grade-status" style="margin-top:12px;"></div>
      <div class="grade-actions" style="margin-top:16px;">
        <button class="back-btn">← 返回</button>
      </div>
    </div>
  `;
  const input = root.querySelector('.grade-file') as HTMLInputElement;
  const status = root.querySelector('.grade-status')!;
  root.querySelector('.back-btn')!.addEventListener('click', onDone);

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    input.disabled = true;
    status.innerHTML = '<p>批改中(AI 判讀,約 10–20 秒)…</p>';
    void gradeAndReflow(root, paper, file, status, onDone);
  });
}

async function gradeAndReflow(
  root: HTMLElement,
  paper: ReviewPaper,
  file: File,
  status: Element,
  onDone: () => void,
): Promise<void> {
  let result: ScanGradeResult;
  try {
    result = await gradeScannedPaper(file, paperToPack(paper));
  } catch {
    status.innerHTML = '<p style="color:#c0392b;">批改失敗,請確認照片清晰後再試一次。</p>';
    const input = root.querySelector('.grade-file') as HTMLInputElement | null;
    if (input) { input.disabled = false; input.value = ''; }
    return;
  }

  const wikiNote = reflowMistakes(paper, result);
  renderResult(root, paper, result, wikiNote, onDone);
}

function renderResult(
  root: HTMLElement,
  paper: ReviewPaper,
  result: ScanGradeResult,
  wikiNote: string,
  onDone: () => void,
): void {
  const byIdx = new Map<number, ReviewQuestion>();
  for (const q of paper.front) byIdx.set(q.idx, q);

  const rows = result.perQuestion.map((pq) => {
    const q = byIdx.get(pq.idx);
    const mark = pq.correct ? '✅' : '❌';
    const promptText = q ? q.prompt : `第 ${pq.idx} 題`;
    const kid = pq.kidAnswer ? escapeHtml(pq.kidAnswer) : '(未作答)';
    const expectedHtml = pq.correct
      ? ''
      : ` <span style="color:#888;">正解 ${escapeHtml(pq.expected)}</span>`;
    return `
      <li style="padding:6px 0;border-bottom:1px solid #eee;">
        <span>${mark}</span>
        <span style="margin-left:6px;">${escapeHtml(promptText)}</span>
        <span style="margin-left:6px;color:#555;">你寫 ${kid}</span>${expectedHtml}
      </li>
    `;
  }).join('');

  const wrong = result.totalCount - result.correctCount;
  const tail = wrong > 0
    ? `✅ 已批改,${wrong} 題答錯已加入錯題本,下次複習會針對加強。`
    : '✅ 已批改,全部答對,太棒了!';

  root.innerHTML = `
    <div class="grade-review-result">
      <h1 style="margin:0 0 4px;">批改結果</h1>
      <p style="margin:0 0 8px;color:#555;">卷號 ${escapeHtml(paper.paperId)}</p>
      <p class="grade-score" style="font-size:22px;font-weight:700;margin:0 0 12px;">${result.correctCount} / ${result.totalCount}</p>
      <ul style="list-style:none;padding:0;margin:0 0 12px;">${rows}</ul>
      <p style="margin:0 0 4px;">${escapeHtml(tail)}</p>
      ${wikiNote ? `<p style="margin:0 0 12px;color:#888;font-size:14px;">${escapeHtml(wikiNote)}</p>` : ''}
      <button class="back-btn">← 返回</button>
    </div>
  `;
  root.querySelector('.back-btn')!.addEventListener('click', onDone);
}

function renderMessage(root: HTMLElement, msg: string, onDone: () => void): void {
  root.innerHTML = `
    <div class="grade-review-msg">
      <p>${escapeHtml(msg)}</p>
      <button class="back-btn">← 返回</button>
    </div>
  `;
  root.querySelector('.back-btn')!.addEventListener('click', onDone);
}

// ── grading → mistake/wiki reflow ────────────────────────────────────────────

/**
 * Record every wrong answer into the mistake log + ingest it into the wiki.
 * Returns a short status note for the result view (wiki sync may fail; that is
 * non-blocking — the mistake log write already succeeded by then).
 */
function reflowMistakes(paper: ReviewPaper, result: ScanGradeResult): string {
  const wrong = result.perQuestion.filter((pq) => !pq.correct);
  if (wrong.length === 0) return '';

  const progress = loadProgress();
  if (!progress) return '';

  const byIdx = new Map<number, ReviewQuestion>();
  for (const q of paper.front) byIdx.set(q.idx, q);

  const sources: IngestSource[] = [];
  for (const pq of wrong) {
    const q = byIdx.get(pq.idx);
    if (!q) continue;
    const questionId = deriveQuestionId(q.prompt);
    recordMistake(progress, {
      questionId,
      kp: q.kp,
      subject: q.subject,
      prompt: q.prompt,
      correctAnswer: q.correct,
      userAnswer: pq.kidAnswer || null,
    });
    sources.push({
      kind: 'mistake',
      subject: q.subject,
      kp: q.kp,
      questionId,
      prompt: q.prompt,
      correctAnswer: q.correct,
    });
  }

  saveProgress(progress);

  // Wiki sync is best-effort: a failure must NOT lose the mistake-log write
  // (already persisted above) nor block the score display.
  if (sources.length > 0 && progress.familyCode) {
    void ingestToWiki(progress.familyCode, sources).catch(() => {
      // swallowed — mistakes are saved locally; wiki will resync next time.
    });
  }

  return '已存入錯題本,wiki 同步稍後重試。';
}

// ── ReviewPaper → QuizPackWithKey ────────────────────────────────────────────

/**
 * Adapt a ReviewPaper's front side into the QuizPackWithKey shape that
 * `gradeScannedPaper` consumes (`questions` drive the AI prompt, `answerKey`
 * drives the lenient compare). MCQ `correctAnswer` is the option index; the
 * answerKey comes straight from the paper (already letters / fill strings).
 */
function paperToPack(paper: ReviewPaper): QuizPackWithKey {
  const questions: GeneratedQuestion[] = paper.front.map((q) => {
    if (q.kind === 'mcq') {
      const options = q.options ?? [];
      // Map the answerKey letter (A/B/C/D) back to an option index for the
      // GeneratedQuestion shape; fall back to 0 when unresolvable.
      const keyEntry = paper.answerKey.find((a) => a.idx === q.idx);
      const idxFromKey = keyEntry ? keyEntry.correct.trim().toUpperCase().charCodeAt(0) - 65 : -1;
      const correctAnswer = idxFromKey >= 0 && idxFromKey < options.length ? idxFromKey : 0;
      return {
        kind: 'mcq',
        prompt: q.prompt,
        explanation: '',
        kp: q.kp,
        options,
        correctAnswer,
      };
    }
    return {
      kind: 'fill',
      prompt: q.prompt,
      explanation: '',
      kp: q.kp,
      correctAnswer: q.correct,
    };
  });

  return {
    packId: paper.paperId,
    subject: paper.front[0]?.subject ?? 'math',
    kpId: paper.front[0]?.kp ?? '',
    questions,
    answerKey: paper.answerKey.map((a) => ({ idx: a.idx, correct: a.correct })),
    generatedAtISO: paper.generatedAtISO,
  };
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}
